import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const ISSUES = [
  { id: 'missing_photos', label: 'Missing or low-quality photos' },
  { id: 'wrong_location', label: 'Inaccurate location' },
  { id: 'price_unreasonable', label: 'Unreasonable price' },
  { id: 'incomplete_description', label: 'Incomplete description' },
  { id: 'duplicate', label: 'Duplicate listing' },
  { id: 'wrong_category', label: 'Wrong category / type' },
  { id: 'contact_missing', label: 'Missing contact info' },
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  listingType: string;
  listingId: string;
  listingTitle: string;
}

export function AuditDialog({ open, onOpenChange, listingType, listingId, listingTitle }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<'passed' | 'needs_revision' | 'rejected'>('passed');
  const [issues, setIssues] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleIssue = (id: string) => {
    setIssues(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    if (!user) return;
    setSubmitting(true);

    // 1. Record the audit
    const { error: auditError } = await supabase.from('listing_audits').insert({
      listing_type: listingType,
      listing_id: listingId,
      auditor_id: user.id,
      status,
      issues,
      notes: notes || null,
    });

    if (auditError) {
      setSubmitting(false);
      toast.error(auditError.message);
      return;
    }

    // 2. Apply the decision to the source listing
    const tableMap: Record<string, 'properties' | 'plots' | 'projects'> = {
      property: 'properties',
      commercial: 'properties',
      plot: 'plots',
      project: 'projects',
    };
    const sourceTable = tableMap[listingType];

    if (sourceTable && status !== 'needs_revision') {

      const { error: updateError } = await supabase
        .from(sourceTable)
        .update({
          approval_status: status === 'passed' ? 'approved' : 'rejected',
          reviewer_notes: notes || null,
          ...(status === 'passed' ? { status: 'active' as const } : {}),
        } as any)
        .eq('id', listingId);

      if (updateError) {
        setSubmitting(false);
        toast.error(`Audit saved but failed to update listing: ${updateError.message}`);
        return;
      }
    }

    setSubmitting(false);
    toast.success(
      status === 'passed' ? 'Listing approved & published' :
      status === 'rejected' ? 'Listing rejected' :
      'Revision requested'
    );
    qc.invalidateQueries({ queryKey: ['audit-queue'] });
    qc.invalidateQueries({ queryKey: ['my-audits'] });
    qc.invalidateQueries({ queryKey: ['property-approvals'] });
    qc.invalidateQueries({ queryKey: ['plot-approvals'] });
    qc.invalidateQueries({ queryKey: ['project-approvals'] });
    onOpenChange(false);
    setIssues([]);
    setNotes('');
    setStatus('passed');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Audit Listing</DialogTitle>
          <p className="text-sm text-muted-foreground line-clamp-1">{listingTitle}</p>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Result</Label>
            <RadioGroup value={status} onValueChange={(v: any) => setStatus(v)}>
              <div className="flex items-center gap-2"><RadioGroupItem value="passed" id="passed" /><Label htmlFor="passed">Passed</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="needs_revision" id="needs" /><Label htmlFor="needs">Needs Revision</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="rejected" id="rejected" /><Label htmlFor="rejected">Rejected</Label></div>
            </RadioGroup>
          </div>

          {status !== 'passed' && (
            <div>
              <Label className="mb-2 block">Issues found</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                {ISSUES.map(iss => (
                  <div key={iss.id} className="flex items-center gap-2">
                    <Checkbox id={iss.id} checked={issues.includes(iss.id)} onCheckedChange={() => toggleIssue(iss.id)} />
                    <Label htmlFor={iss.id} className="font-normal cursor-pointer">{iss.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="notes" className="mb-2 block">Notes</Label>
            <Textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional comments..." rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>Submit Audit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
